import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { MessageBusService } from '../message-bus.service';
import { SessionService } from '../authentication/session-service.service';
import { isVoid } from '../utils';

/** The QG cursor messages' payload shape (anonymous bus payloads — there is no DTO class; the
 *  publisher is QuoteGeneratorService). `ticker` is the STREAM (underlying) ticker, never an option code. */
interface QgCursorDatePayload {
   ticker: string;
   cursorDate: string;
}

/**
 * Collars QG-cursor reader — the "now" the panel's DTE displays count from in QuoteGenerator HISTORY mode.
 *
 * Canonical platform pattern (package-comparison / hedge-matrix / OPG): the QG cursor is PER-STREAM state
 * keyed by the UNDERLYING ticker. 'Qg.CursorDateChangedMessage' broadcasts it on every history quote tick
 * (and on a jump); 'Qg.CursorDateRequest' answers synchronously by in-place payload mutation (valid because
 * MessageBusService.publish dispatches via Subject.next synchronously).
 *
 * Reads are request-per-call (no value cache — a removed stream must fall back to the wall clock, not a
 * dead cached day); the broadcast feed only tracks each stream's last DAY part to signal `dayChanged$`
 * when it flips (DTE only changes when the day changes — the same dedupe the other consumers apply), so
 * DTE-bearing views can repaint. Outside a QG shell it returns undefined and
 * `daysToExpiration(exp, undefined)` falls back to the real wall clock — callers need no mode branch.
 */
@Injectable()
export class CollarsQgCursorService {

   constructor(
      private readonly _messageBus: MessageBusService,
      private readonly _sessionService: SessionService
   ) {
      // Service lifetime = app lifetime (module provider, created with the first Collars panel) — the
      // subscription is deliberately never torn down, same as the QuoteGeneratorService responder.
      this._messageBus.of<QgCursorDatePayload>('Qg.CursorDateChangedMessage')
         .subscribe(msg => this.onCursorDateChanged(msg.payload));
   }

   // PREVIOUS broadcast day ('yyyy-MM-dd') per stream ticker — the day-flip detector behind dayChanged$
   // ONLY, never served as a value (reads go through the per-call request below, so a removed stream
   // self-heals to the wall-clock fallback instead of being served from a dead cache entry). Day part
   // only: every intra-day cursor time yields the same DTE (daysToExpiration anchors the expiration to
   // end-of-day ET), so the per-tick message stream dedupes to one signal per emulated day.
   private readonly _cursorDayByUl: { [underlying: string]: string } = {};

   private readonly _dayChanged = new Subject<void>();

   /** Fires when any underlying's cursor DAY changes (or its cursor clears) — DTE text is stale, repaint. */
   get dayChanged$(): Observable<void> {
      return this._dayChanged.asObservable();
   }

   /**
    * The cursor day ('yyyy-MM-dd') for the given UNDERLYING ticker, or undefined when there is none
    * (non-QG shell, or no registered stream for that underlying) — undefined makes daysToExpiration /
    * makeFullExpirationDate fall back to the real wall clock, which is the correct live-mode behavior.
    */
   cursorDayFor(underlying: string): string {
      if (isVoid(underlying)) { return undefined; }
      // Live shells never run the QG responder/broadcaster — skip the bus round-trip entirely.
      const login = this._sessionService.loginResult;
      if (!login || !login.isQuoteGeneratorShell) { return undefined; }

      // Request-per-call (the hedge-matrix getCursorDate() pattern): synchronous request/reply by
      // in-place mutation, NO value cache. A removed/unregistered stream leaves cursorDate undefined
      // (the responder's find misses) and the caller falls back to the wall clock — serving a cached
      // value here would keep counting from a dead stream's last cursor day instead.
      const msg: QgCursorDatePayload = { ticker: underlying, cursorDate: undefined };
      this._messageBus.publish({ topic: 'Qg.CursorDateRequest', payload: msg });
      if (isVoid(msg.cursorDate)) { return undefined; }
      return msg.cursorDate.split('T')[0];
   }

   private onCursorDateChanged(payload: QgCursorDatePayload): void {
      const ticker = payload ? payload.ticker : null;
      if (isVoid(ticker)) { return; }
      // A void cursorDate means the stream's cursor is gone — drop the entry so reads fall back to the
      // wall clock (consumer norm: OPG/volatility-header null their cache on void).
      if (isVoid(payload.cursorDate)) {
         if (this._cursorDayByUl[ticker]) {
            delete this._cursorDayByUl[ticker];
            this._dayChanged.next();
         }
         return;
      }
      const day = payload.cursorDate.split('T')[0];
      if (this._cursorDayByUl[ticker] === day) { return; }   // same-day tick — DTE unchanged
      this._cursorDayByUl[ticker] = day;
      this._dayChanged.next();
   }
}
